import { useEffect, useState } from 'react';
import { Badge } from './badge';
import { Wifi, WifiOff, Loader2 } from 'lucide-react';
import { processQueue, getPendingSignatures } from '@/lib/offlineQueue';
import { flushTrainingOutbox } from '@/lib/trainingOutbox';
import { useAuth } from '@/hooks/useAuth';

export function NetworkStatus() {
  const { user } = useAuth();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    if (!user) return;
    
    const updatePendingCount = async () => {
      const pending = await getPendingSignatures(user.id);
      setPendingCount(pending.length);
    };
    
    updatePendingCount();
    const interval = setInterval(updatePendingCount, 5000);
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    const handleOnline = async () => {
      setIsOnline(true);
      setIsSyncing(true);
      try {
        await processQueue();
        await flushTrainingOutbox();
      } finally {
        setIsSyncing(false);
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (isOnline && !isSyncing) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <Badge
        variant={isOnline ? 'default' : 'destructive'}
        className="flex items-center gap-2 px-3 py-2 text-sm shadow-lg"
      >
        {isSyncing ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Syncing...
          </>
        ) : isOnline ? (
          <>
            <Wifi className="h-4 w-4" />
            Online
          </>
        ) : (
          <>
            <WifiOff className="h-4 w-4" />
            Offline Mode
            {pendingCount > 0 && (
              <span className="ml-2 bg-orange-600 text-white rounded-full px-2 py-0.5 text-xs font-medium">
                {pendingCount}
              </span>
            )}
          </>
        )}
      </Badge>
    </div>
  );
}
